import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePrixAgent, usePrixUsine, useProduits, useUsines } from "@/lib/donnees";
import { autorise, useNiveau, useSession } from "@/lib/session";
import { indexPrixUsine, margeDe, planchersDe } from "@/domain/prix";
import { Kpi, Kpis, Section, Segment, Tag } from "@/ui/primitives";
import { Table, dansUnChamp } from "@/ui/Table";
import type { Colonne } from "@/ui/Table";
import { euro, entier } from "@/lib/format";
import type { Produit } from "@/types/metier";

export function Catalogue() {
  const navigue = useNavigate();
  const { donnees: produits, chargement, erreur } = useProduits();
  const { donnees: usines } = useUsines();
  const { donnees: lignesPrixUsine } = usePrixUsine();
  const { donnees: prixAgent } = usePrixAgent();
  const niveau = useNiveau();
  const { profil } = useSession();
  const voitUsine = autorise(niveau, "agent");
  const direction = niveau === "admin";
  const [usine, setUsine] = useState<string | null>(null);
  const [gamme, setGamme] = useState<string | null>(null);

  /* Le plancher dépend de qui regarde : chacun voit le sien. La direction, qui
     n'a pas de tarif propre, voit le prix de référence du produit. */
  const planchers = useMemo(
    () => planchersDe(produits, prixAgent, profil?.id ?? ""),
    [produits, prixAgent, profil?.id],
  );

  /* Vide pour qui n'est pas de la direction : la base ne rend aucune ligne. */
  const prixUsine = useMemo(() => indexPrixUsine(lignesPrixUsine), [lignesPrixUsine]);

  const nomUsine = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of usines) m.set(u.id, voitUsine ? u.nom : (u.categorie ?? "—"));
    return m;
  }, [usines, voitUsine]);

  const base = useMemo(
    () => produits.filter((p) => (usine ? p.usine_id === usine : true)),
    [produits, usine],
  );

  const gammes = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of base) if (p.gamme) m.set(p.gamme, (m.get(p.gamme) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [base]);

  const lignes = useMemo(
    () => base.filter((p) => (gamme ? p.gamme === gamme : true)),
    [base, gamme],
  );

  const tarifes = produits.filter((p) => (planchers.get(p.id) ?? 0) > 0);
  const moyen = tarifes.length
    ? tarifes.reduce((s, p) => s + (planchers.get(p.id) ?? 0), 0) / tarifes.length
    : 0;

  /* Marge moyenne : sur les seules références dont on connaît les deux prix.
     Faire autrement reviendrait à compter zéro pour un prix usine manquant. */
  const marges = direction
    ? produits
        .map((p) => margeDe(planchers.get(p.id) ?? null, prixUsine.get(p.id) ?? null))
        .filter((m): m is number => m !== null)
    : [];
  const margeMoyenne = marges.length ? marges.reduce((s, m) => s + m, 0) / marges.length : 0;

  const colonnes: (Colonne<Produit> | false)[] = [
    {
      cle: "ref",
      titre: "Référence",
      type: "ref",
      filtre: "texte",
      sous: (p) => p.nom,
    },
    { cle: "nom", titre: "Désignation", classe: "name", filtre: "texte", mobile: false },
    { cle: "gamme", titre: "Gamme", mobile: false, filtre: "select",
      rendu: (p) => (p.gamme ? <Tag>{p.gamme}</Tag> : "—") },
    { cle: "conditionnement", titre: "Cond.", type: "ref", mobile: false },
    {
      cle: "prix",
      titre: direction ? "Prix de vente" : "Mon prix mini",
      court: "Prix",
      type: "euro",
      total: true,
      agregat: "moyenne",
      totalTitre: "Prix moyen",
      nombre: (p) => planchers.get(p.id) ?? 0,
      rendu: (p) => {
        const v = planchers.get(p.id);
        return v ? euro(v) : <span className="locked">sans prix</span>;
      },
    },
    direction && {
      cle: "prix_usine",
      titre: "Prix usine",
      court: "Usine",
      type: "euro",
      mobile: false,
      total: true,
      agregat: "moyenne",
      totalTitre: "Prix usine moyen",
      nombre: (p) => prixUsine.get(p.id) ?? 0,
      rendu: (p) => {
        const v = prixUsine.get(p.id);
        return v ? euro(v) : <span className="locked">inconnu</span>;
      },
    },
    direction && {
      cle: "marge",
      titre: "Marge",
      type: "euro",
      mobile: false,
      total: true,
      agregat: "moyenne",
      totalTitre: "Marge moyenne",
      nombre: (p) => margeDe(planchers.get(p.id) ?? null, prixUsine.get(p.id) ?? null) ?? 0,
      rendu: (p) => {
        const m = margeDe(planchers.get(p.id) ?? null, prixUsine.get(p.id) ?? null);
        if (m === null) return <span className="locked">—</span>;
        return <span className={m < 0 ? "marge-ko" : ""}>{euro(m)}</span>;
      },
    },
    voitUsine && {
      cle: "usine_id",
      titre: "Usine",
      mobile: false,
      filtre: "select",
      texte: (p) => nomUsine.get(p.usine_id) ?? "—",
    },
  ];

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;

  return (
    <>
      <div className="filterbar">
        <Segment
          label="Usine"
          valeur={usine}
          onChange={(v) => {
            setUsine(v);
            setGamme(null);
          }}
          options={[
            { cle: null, nom: voitUsine ? "Toutes les usines" : "Tout le catalogue" },
            ...usines.map((u) => ({
              cle: u.id,
              nom: voitUsine ? u.nom : (u.categorie ?? u.code),
              n: produits.filter((p) => p.usine_id === u.id).length,
            })),
          ]}
        />
        {gammes.length > 1 ? (
          <Segment
            label="Gamme"
            valeur={gamme}
            onChange={setGamme}
            options={[
              { cle: null, nom: "Toutes gammes" },
              ...gammes.map(([g, n]) => ({ cle: g, nom: g, n })),
            ]}
          />
        ) : null}
      </div>

      <div className="pad">
        <Kpis>
          <Kpi k="Références" v={entier(produits.length)} n={`${usines.length} usines`} />
          <Kpi
            k={direction ? "Prix de vente moyen" : "Mon prix mini moyen"}
            v={euro(moyen)}
            n="sur les références tarifées"
          />
          {direction ? (
            <Kpi
              k="Marge moyenne"
              v={euro(margeMoyenne)}
              n={`sur ${entier(marges.length)} références au prix usine connu`}
            />
          ) : (
            <Kpi
              k="Sans prix"
              v={entier(produits.length - tarifes.length)}
              n="non vendables en l'état"
            />
          )}
        </Kpis>

        <Section titre="Catalogue" note={chargement ? "chargement…" : `${lignes.length} références`}>
          <Table
            lignes={lignes}
            cle={(p) => p.id}
            colonnes={colonnes}
            chercher="Référence ou désignation"
            onClic={(p, ev) => {
              if (!dansUnChamp(ev)) navigue(`/catalogue/${p.id}`);
            }}
          />
        </Section>
      </div>
    </>
  );
}
